import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { ClipboardList, MessageCircle, BadgeCheck } from 'lucide-react'

const steps = [
  {
    number: '01',
    title: 'Preencha o formulário',
    description: 'Leva menos de 1 minuto. Só precisamos do seu nome, e-mail e especialidade.',
    icon: <ClipboardList className="w-6 h-6 text-white" />,
  },
  {
    number: '02',
    title: 'Aguarde nosso contato',
    description: 'Nossa equipe entra em contato com você antes do lançamento com tudo que precisa saber.',
    icon: <MessageCircle className="w-6 h-6 text-white" />,
  },
  {
    number: '03',
    title: 'Acesse com vantagens de pioneiro',
    description: 'Quem entrou na lista garante condições especiais e acesso antes de todo mundo.',
    icon: <BadgeCheck className="w-6 h-6 text-white" />,
  },
]

export default function HowItWorks() {
  const containerRef = useRef(null)

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 50%', 'end 95%'],
  })

  const widthPercentage  = useTransform(scrollYProgress, [0, 1], ['0%', '100%'])
  const heightPercentage = useTransform(scrollYProgress, [0, 1], ['0%', '100%'])

  const iconMotionProps = [
    {
      scale:     useTransform(scrollYProgress, [0,    0.05], [0.85, 1]),
      filter:    useTransform(scrollYProgress, [0,    0.05], ['grayscale(100%)', 'grayscale(0%)']),
      opacity:   useTransform(scrollYProgress, [0,    0.05], [0.4, 1]),
      boxShadow: useTransform(scrollYProgress, [0,    0.05], ['0 0 0px rgba(0,122,255,0)', '0 0 25px rgba(0,122,255,0.5)']),
    },
    {
      scale:     useTransform(scrollYProgress, [0.4,  0.5],  [0.85, 1]),
      filter:    useTransform(scrollYProgress, [0.4,  0.5],  ['grayscale(100%)', 'grayscale(0%)']),
      opacity:   useTransform(scrollYProgress, [0.4,  0.5],  [0.4, 1]),
      boxShadow: useTransform(scrollYProgress, [0.4,  0.5],  ['0 0 0px rgba(0,122,255,0)', '0 0 25px rgba(0,122,255,0.5)']),
    },
    {
      scale:     useTransform(scrollYProgress, [0.85, 0.95], [0.85, 1]),
      filter:    useTransform(scrollYProgress, [0.85, 0.95], ['grayscale(100%)', 'grayscale(0%)']),
      opacity:   useTransform(scrollYProgress, [0.85, 0.95], [0.4, 1]),
      boxShadow: useTransform(scrollYProgress, [0.85, 0.95], ['0 0 0px rgba(0,122,255,0)', '0 0 25px rgba(0,122,255,0.5)']),
    },
  ]

  const mIconMotionProps = [
    {
      scale:     useTransform(scrollYProgress, [0,    0.05], [0.85, 1]),
      filter:    useTransform(scrollYProgress, [0,    0.05], ['grayscale(100%)', 'grayscale(0%)']),
      opacity:   useTransform(scrollYProgress, [0,    0.05], [0.4, 1]),
      boxShadow: useTransform(scrollYProgress, [0,    0.05], ['0 0 0px rgba(0,122,255,0)', '0 0 25px rgba(0,122,255,0.5)']),
    },
    {
      scale:     useTransform(scrollYProgress, [0.28, 0.38], [0.85, 1]),
      filter:    useTransform(scrollYProgress, [0.28, 0.38], ['grayscale(100%)', 'grayscale(0%)']),
      opacity:   useTransform(scrollYProgress, [0.28, 0.38], [0.4, 1]),
      boxShadow: useTransform(scrollYProgress, [0.28, 0.38], ['0 0 0px rgba(0,122,255,0)', '0 0 25px rgba(0,122,255,0.5)']),
    },
    {
      scale:     useTransform(scrollYProgress, [0.6,  0.7],  [0.85, 1]),
      filter:    useTransform(scrollYProgress, [0.6,  0.7],  ['grayscale(100%)', 'grayscale(0%)']),
      opacity:   useTransform(scrollYProgress, [0.6,  0.7],  [0.4, 1]),
      boxShadow: useTransform(scrollYProgress, [0.6,  0.7],  ['0 0 0px rgba(0,122,255,0)', '0 0 25px rgba(0,122,255,0.5)']),
    },
  ]

  return (
    <section
      id="como-funciona"
      ref={containerRef}
      className="w-full bg-surface-page py-16 md:py-24 overflow-hidden relative"
    >
      <div className="max-w-7xl mx-auto px-6 w-full flex flex-col items-center text-center">
        <h2 className="text-[26px] md:text-[32px] font-bold text-text-primary tracking-tight mb-3">
          Garanta sua vaga em <span className="text-accent-primary">3 passos</span>
        </h2>
        <p className="text-base md:text-[18px] text-text-secondary">Simples, rápido e sem compromisso.</p>

        <div className="hidden md:flex flex-col w-full relative mt-16 max-w-[900px]">
          <div className="absolute top-7 left-[16.66%] right-[16.66%] h-[2px] bg-[rgba(0,122,255,0.15)] z-0" />

          <div className="absolute top-7 left-[16.66%] w-[66.66%] h-[2px] z-10 flex items-center">
            <motion.div
              className="h-full bg-gradient-to-r from-transparent via-[#007AFF] to-[#00d4ff] relative"
              style={{ width: widthPercentage }}
            >
              <div className={'absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 bg-white rounded-full shadow-[0_0_12px_4px_rgba(0,180,255,0.7)]'} />
            </motion.div>
          </div>

          <div className="flex justify-between w-full relative z-20">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center relative w-1/3">
                <motion.div className={'w-14 h-14 rounded-full flex items-center justify-center border-2 border-white bg-[#007AFF] mb-6'} style={iconMotionProps[index]}>
                  {step.icon}
                </motion.div>
                <span className="font-mono text-[11px] font-bold text-accent-primary mb-2">
                  STEP {step.number}
                </span>
                <h4 className="text-[18px] font-bold text-text-primary mb-2">{step.title}</h4>
                <p className="text-[14px] text-text-secondary max-w-[200px] mx-auto">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="md:hidden flex flex-col w-full relative pl-8 mt-12 text-left">
          <div className="absolute top-0 bottom-0 left-[35px] w-[2px] bg-[rgba(0,122,255,0.15)] z-0" />

          <div className="absolute top-0 left-[35px] w-[2px] h-full z-10 flex flex-col items-center">
            <motion.div
              className="w-full bg-gradient-to-b from-transparent via-[#007AFF] to-[#00d4ff] relative"
              style={{ height: heightPercentage }}
            >
              <div className={'absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-[0_0_12px_4px_rgba(0,180,255,0.7)]'} />
            </motion.div>
          </div>

          <div className="flex flex-col gap-16 relative z-20 w-full">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-8">
                <motion.div
                  className="w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center border-2 border-white bg-[#007AFF] relative -ml-[19px]"
                  style={mIconMotionProps[index]}
                >
                  {step.icon}
                </motion.div>
                <div className="flex flex-col pt-1">
                  <span className="font-mono text-[11px] font-bold text-accent-primary mb-1">
                    STEP {step.number}
                  </span>
                  <h4 className="text-[16px] font-bold text-text-primary mb-2">{step.title}</h4>
                  <p className="text-[14px] text-text-secondary leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  )
}
